/**
 * Откуда окно выгрузки узнаёт, что происходит с задачей.
 *
 * Два источника, а не один:
 *
 *  1) события фоновых задач (WebSocket) — быстрые, но их легко пропустить:
 *     задача, отказавшая за полсекунды, успевает закрыться раньше, чем окно
 *     подпишется, а завершённую задачу хранилище фоновых задач выбрасывает
 *     через пять секунд после финального события;
 *  2) запрос состояния задачи — медленнее, зато отвечает на вопрос «что с ней
 *     сейчас» независимо от того, что окно услышало. Делается сразу при
 *     открытии и повторяется, пока задача не в терминальном состоянии.
 *
 * Всё, что приходит из любого источника, складывается в реестр выгрузок:
 * итоги — в `results`, состояние — в `job`. Поэтому содержимое окна переживает
 * и уборку виджета фоновых задач, и закрытие окна, и перезагрузку страницы.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useBackgroundJobsStore } from '../../stores/backgroundJobsStore'
import { publishCardsAPI } from './api'
import { usePublishJobsStore } from './publishJobsStore'
import { extractPublishResults, isTerminalJobStatus, snapshotFromJob } from './publishScope'
import type { PublishJobSnapshot } from './types'

/** Как часто переспрашиваем состояние незавершённой задачи */
export const PUBLISH_JOB_POLL_INTERVAL_MS = 3000

/**
 * Сколько ждём подтверждения запуска, прежде чем сказать человеку, что что-то
 * не так. Пятнадцать секунд — заведомо больше нормального пути «создать задачу
 * → событие → ответ на запрос состояния», и заведомо меньше терпения человека
 * перед пустым прогрессом.
 */
export const PUBLISH_JOB_HANDSHAKE_TIMEOUT_MS = 15000

/** Как часто пересчитывается «ждём слишком долго» */
const HANDSHAKE_TICK_MS = 1000

export interface PublishJobFeed {
  /** Последнее известное состояние задачи; null — задача ещё не отозвалась */
  snapshot: PublishJobSnapshot | null
  /** Задача уже ничего не пришлёт */
  finished: boolean
  /** Подтверждения запуска нет дольше разумного */
  handshakeOverdue: boolean
  /** Идёт запрос состояния задачи */
  refreshing: boolean
  /** Спросить состояние сейчас (кнопка «Проверить снова») */
  refresh: () => void
}

export function usePublishJobFeed(jobId: string | null, isOpen: boolean): PublishJobFeed {
  const liveJob = useBackgroundJobsStore((state) => (jobId ? state.jobs.get(jobId) : undefined))
  const snapshot = usePublishJobsStore((state) => (jobId ? state.records[jobId]?.job ?? null : null))
  const startedAt = usePublishJobsStore((state) =>
    jobId ? state.records[jobId]?.startedAt ?? null : null,
  )
  const mergeResults = usePublishJobsStore((state) => state.mergeResults)
  const mergeSnapshot = usePublishJobsStore((state) => state.mergeSnapshot)

  const [refreshing, setRefreshing] = useState(false)
  // Форсированный перезапуск опроса кнопкой «Проверить снова»
  const [nonce, setNonce] = useState(0)
  // Пересчёт «ждём слишком долго»: сам по себе рендер не случится
  const [, setTick] = useState(0)

  // Отсчёт ожидания ведём от открытия окна этой задачи, а не от монтирования
  // компонента: окно висит на странице всё время и открывается по требованию.
  const session = useRef({ key: '', at: Date.now() })
  const sessionKey = isOpen && jobId ? jobId : ''
  if (session.current.key !== sessionKey) session.current = { key: sessionKey, at: Date.now() }

  const finished = isTerminalJobStatus(snapshot?.status)
  const finishedRef = useRef(finished)
  finishedRef.current = finished

  // Событие фоновой задачи → реестр. Забирается и итог, и состояние: событие
  // может оказаться единственным, что окно вообще увидит.
  useEffect(() => {
    if (!jobId || !liveJob) return
    const results = extractPublishResults(liveJob)
    if (results.length) mergeResults(jobId, results)
    const next = snapshotFromJob(liveJob)
    if (next) mergeSnapshot(jobId, next)
  }, [jobId, liveJob, mergeResults, mergeSnapshot])

  // Опрос состояния задачи: первый заход — сразу при открытии окна, поэтому
  // задача, успевшая завершиться до подписки, показывается полностью.
  useEffect(() => {
    if (!isOpen || !jobId) return
    let stopped = false
    let timer: ReturnType<typeof setTimeout> | null = null

    const pull = async (): Promise<void> => {
      setRefreshing(true)
      try {
        const job = await publishCardsAPI.fetchPublishJob(jobId)
        if (stopped) return
        if (job) {
          const results = extractPublishResults(job)
          if (results.length) mergeResults(jobId, results)
          const next = snapshotFromJob(job)
          if (next) mergeSnapshot(jobId, next)
        }
      } catch {
        // Сеть моргнула — следующий заход. Ронять окно из-за опроса нельзя:
        // выгрузка на сервере от этого не зависит.
      } finally {
        if (!stopped) setRefreshing(false)
      }
      if (stopped || finishedRef.current) return
      timer = setTimeout(() => void pull(), PUBLISH_JOB_POLL_INTERVAL_MS)
    }

    void pull()

    return () => {
      stopped = true
      if (timer) clearTimeout(timer)
    }
  }, [isOpen, jobId, nonce, mergeResults, mergeSnapshot])

  // Пока задача молчит — тикаем, чтобы вовремя сказать об этом человеку.
  useEffect(() => {
    if (!isOpen || snapshot) return
    const id = setInterval(() => setTick((value) => value + 1), HANDSHAKE_TICK_MS)
    return () => clearInterval(id)
  }, [isOpen, snapshot])

  const refresh = useCallback(() => setNonce((value) => value + 1), [])

  const parsedStart = startedAt ? Date.parse(startedAt) : NaN
  const baseline = Number.isFinite(parsedStart) ? parsedStart : session.current.at
  const handshakeOverdue =
    isOpen && !snapshot && Date.now() - baseline > PUBLISH_JOB_HANDSHAKE_TIMEOUT_MS

  return { snapshot, finished, handshakeOverdue, refreshing, refresh }
}

export default usePublishJobFeed
